/**
 * useAdminPools Hook - Phase 3H-A
 *
 * Fetches pools with admin-specific data and actions.
 * Added: deletePool function
 */

import { useState, useEffect, useCallback } from 'react';
import { AdminPool, AdminCreatePoolRequest, AdminUpdatePoolRequest } from '../types';
import { AdminPoolService } from '../api';

interface UseAdminPoolsFilters {
  status?: string;
  domain?: string;
  mode?: string;
}

interface UseAdminPoolsReturn {
  pools: AdminPool[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  setFilters: (filters: UseAdminPoolsFilters) => void;
  refetch: () => void;
  createPool: (data: AdminCreatePoolRequest) => Promise<AdminPool>;
  updatePool: (poolId: string, data: AdminUpdatePoolRequest) => Promise<AdminPool>;
  lockPool: (poolId: string, reason?: string) => Promise<AdminPool>;
  deletePool: (poolId: string, hardDelete?: boolean) => Promise<void>;
  creating: boolean;
  updating: boolean;
  deleting: boolean;
  actionError: string | null;
}

export function useAdminPools(): UseAdminPoolsReturn {
  const [pools, setPools] = useState<AdminPool[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<UseAdminPoolsFilters>({});
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchPools = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const offset = (page - 1) * pageSize;
      const response = await AdminPoolService.getAll({
        ...filters,
        limit: pageSize,
        offset,
      });
      setPools(response.pools);
      setTotal(response.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pools');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchPools();
  }, [fetchPools]);

  const createPool = useCallback(async (data: AdminCreatePoolRequest): Promise<AdminPool> => {
    setCreating(true);
    setActionError(null);

    try {
      const pool = await AdminPoolService.create(data);
      // Refetch to update list
      fetchPools();
      return pool;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create pool';
      setActionError(message);
      throw err;
    } finally {
      setCreating(false);
    }
  }, [fetchPools]);

  const updatePool = useCallback(async (poolId: string, data: AdminUpdatePoolRequest): Promise<AdminPool> => {
    setUpdating(true);
    setActionError(null);

    try {
      const pool = await AdminPoolService.update(poolId, data);
      // Update local state
      setPools(prev => prev.map(p => p.pool_id === poolId ? pool : p));
      return pool;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update pool';
      setActionError(message);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const lockPool = useCallback(async (poolId: string, reason?: string): Promise<AdminPool> => {
    setUpdating(true);
    setActionError(null);

    try {
      const pool = await AdminPoolService.lock(poolId, reason ? { reason } : undefined);
      // Update local state
      setPools(prev => prev.map(p => p.pool_id === poolId ? pool : p));
      return pool;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to lock pool';
      setActionError(message);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  const deletePool = useCallback(async (poolId: string, hardDelete: boolean = false): Promise<void> => {
    setDeleting(true);
    setActionError(null);

    try {
      await AdminPoolService.delete(poolId, hardDelete);
      // Refetch to update list
      fetchPools();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete pool';
      setActionError(message);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, [fetchPools]);

  return {
    pools,
    total,
    loading,
    error,
    page,
    pageSize,
    setPage,
    setPageSize,
    setFilters,
    refetch: fetchPools,
    createPool,
    updatePool,
    lockPool,
    deletePool,
    creating,
    updating,
    deleting,
    actionError,
  };
}
